import usePendingActionsBusinessProfile from '@hooks/query/pendingActions/usePendingActionsBusinessProfile';
import { Ambulancia, BusinessAccount, PatientsInactive, Pracovnici, ProgressArrows2 } from '@icon';
import { PageHeaderWrapper, TabItemProps, Tabs, useAuthorizedCheck } from '@ui';
import React from 'react';
import { useIntl } from 'react-intl';
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { TAuthority } from 'types/authority.types';
import Departments from './Departments';
import Overview from './Overview';
import ProviderPatients from './ProviderPatients';
import SoftwareProviders from './software-providers';
import Staff from './Staff';

interface BusinessProfileProps {}

type TTabItem = TabItemProps & { authority?: TAuthority[]; Component: JSX.Element };

const BusinessProfileLayout: React.FC<BusinessProfileProps> = () => {
  const { formatMessage } = useIntl();
  const match = useRouteMatch();
  const { path } = match;
  const checkAuthority = useAuthorizedCheck();

  const history = useHistory();
  const location = useLocation();
  const { data } = usePendingActionsBusinessProfile({ refetchInterval: 60000 });

  const tabs = React.useMemo(() => {
    const result: TTabItem[] = [
      {
        label: formatMessage({ id: 'overview_title' }),
        key: `${path}/overview`,
        Icon: BusinessAccount,
        authority: undefined,
        Component: <Route exact path={`${path}/overview`} component={Overview} />,
      },
      {
        label: formatMessage({ id: 'departments_title' }),
        key: `${path}/departments`,
        Icon: Ambulancia,
        authority: ['workspaceManagementView', 'workspaceManagementEdit'],
        Component: <Route exact path={`${path}/departments`} component={Departments} />,
      },
      {
        label: formatMessage({ id: 'staff_table_title' }),
        key: `${path}/staff`,
        Icon: Pracovnici,
        authority: ['workerManagementView', 'workerManagementEdit'],
        Component: <Route exact path={`${path}/staff`} component={Staff} />,
      },
      {
        label: 'Pacienti',
        key: `${path}/patients`,
        Icon: PatientsInactive,
        badge: data?.pendingClientPairingCount,
        authority: ['clientManagementView', 'clientManagementEdit'],
        Component: <Route exact path={`${path}/patients`} component={ProviderPatients} />,
      },
      {
        label: 'Externé služby',
        key: `${path}/software-providers`,
        Icon: ProgressArrows2,
        authority: ['apiKeyManagementView', 'apiKeyManagementEdit'],
        Component: <Route path={`${path}/software-providers`} component={SoftwareProviders} />,
      },
    ];

    return result.filter((page) => checkAuthority(page.authority));
  }, [path, formatMessage, data, checkAuthority]);

  return (
    <PageHeaderWrapper
      showDivider
      title={formatMessage({ id: 'sidebar.business-profile' })}
      footer={
        <Tabs
          items={tabs}
          onChange={(key) => {
            history.push(key);
          }}
          activeKey={location.pathname}
        />
      }
    >
      <Switch>
        {tabs.map((t) => t.Component)}
        <Redirect to={`${path}/overview`} />
      </Switch>
    </PageHeaderWrapper>
  );
};

export default React.memo(BusinessProfileLayout);
